import { useState, useCallback, useEffect, useMemo } from "react";
import useTripStore from "@/stores/tripBuilder/useTrip";
import useLocationManager from "@/hooks/useLocationManager";
import { useRateLimitStore } from "@/stores/rateLimitStore";
import useAuthStore from "@/stores/user/authStore";

export const useTripBuilderState = () => {
  const [isAddDestinationModalOpen, setIsAddDestinationModalOpen] =
    useState(false);
  const [tripLoading, setTripLoading] = useState(true);

  // Extract only what we need from stores
  const setRateLimit = useRateLimitStore((state) => state.setRateLimit);
  const anonymousUser = useAuthStore((state) => state.anonymousUser);

  // Get trip ID from URL - memoize to prevent recalculation
  const tripId = useMemo(() => window.location.pathname.split("/").pop(), []);

  // Core trip state - selective extraction to minimize re-renders
  const fetchTripData = useTripStore((state) => state.fetchTripData);
  const tripData = useTripStore((state) => state.tripData);
  const recordView = useTripStore((state) => state.recordView);
  const isParticipant = useTripStore((state) => state.isParticipant);
  const setTripData = useTripStore((state) => state.setTripData);

  const { handleLocationClick } = useLocationManager();

  // Memoize callback to prevent unnecessary recreations
  const setModalOpen = useCallback(
    (open: boolean) => {
      if (!tripData) return;
      if (anonymousUser && tripData?.locations.length >= 2) {
        setRateLimit(
          "Create your account now to add more locations to your trip!",
          tripData.name
        );
        return;
      }
      setIsAddDestinationModalOpen(open);
    },
    [anonymousUser, tripData, setRateLimit]
  );

  // Initialize trip when data is loaded - memoized
  const initializeTrip = useCallback(() => {
    if (!tripData || !handleLocationClick) return;

    const locations = tripData.locations;
    if (!locations || locations.length === 0) return;

    const lastLocation = locations[locations.length - 1];
    if (lastLocation) {
      handleLocationClick(lastLocation);
    } else if (locations[0]) {
      handleLocationClick(locations[0]);
    }

    // Show add destination modal by default only when first loading with one location
    if (locations.length === 1) {
      setModalOpen(true);
    }

    setTripLoading(false);
  }, [tripData, handleLocationClick, setModalOpen]);

  // Effect for initial data fetching
  useEffect(() => {
    if (!tripId) return;

    setTripData(null);
    fetchTripData(tripId);
  }, [tripId, fetchTripData, setTripData]);

  // Effect for initializing trip after data is loaded
  useEffect(() => {
    if (tripLoading && tripData && tripData.id === tripId) {
      initializeTrip();
    }
  }, [tripLoading, tripData, tripId, initializeTrip]);

  // Effect for view duration tracking
  useEffect(() => {
    if (!tripData?.id || tripLoading) return;

    const startTime = Date.now();
    return () => {
      const duration = (Date.now() - startTime) / 1000;
      if (duration >= 2) {
        recordView(tripData.id, duration);
      }
    };
  }, [tripData?.id, tripLoading, recordView]);

  // Get resetState function directly from store to avoid re-renders
  const cleanupTripBuilder = useMemo(
    () => useTripStore.getState().resetState,
    []
  );

  return {
    isAddDestinationModalOpen,
    tripLoading,
    tripId,
    tripData,
    isParticipant,
    setModalOpen,
    cleanupTripBuilder,
  };
};
